import { motion, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import { normalizeOrderTypeId } from '@/lib/orderTypeSettings'

type GlyphProps = {
  active: boolean
  className?: string
  /** Used for generic custom types (stroke inherits via currentColor from parent) */
  accentHex?: string
}

/**
 * Motion-animated order-type glyphs (lucide-animated style): Lucide-grade
 * artwork drawn inline so individual parts can move. Loops only run while the
 * segment is selected, a soft scale "pop" plays on activation, and everything
 * goes static under prefers-reduced-motion. Strokes inherit `currentColor`
 * from the segment, so configured order-type colors keep working.
 */

const strokeProps = {
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  fill: 'none',
} as const

function useGlyphMotion(active: boolean) {
  const reduced = useReducedMotion()
  return {
    animate: !reduced && active,
    pop:
      !reduced && active
        ? { scale: [1, 1.12, 1], transition: { duration: 0.45, ease: 'easeOut' as const } }
        : { scale: 1 },
  }
}

/** Dine in — cloche on a plate: lid bobs while steam wisps rise. */
function DineInGlyph({ active, className }: GlyphProps) {
  const { animate, pop } = useGlyphMotion(active)
  return (
    <motion.svg
      viewBox="0 0 24 24"
      className={cn('h-6 w-6 shrink-0', className)}
      aria-hidden
      animate={pop}
    >
      {/* steam wisps */}
      {[
        { d: 'M8.8 7.5c-.3-1.1.6-2 .3-3.3', delay: 0 },
        { d: 'M12.1 6.8c-.3-1.1.6-2 .3-3.3', delay: 0.45 },
        { d: 'M15.4 7.5c-.3-1.1.6-2 .3-3.3', delay: 0.9 },
      ].map((s) => (
        <motion.path
          key={s.d}
          d={s.d}
          {...strokeProps}
          strokeWidth={1.5}
          animate={
            animate
              ? { y: [0.6, -1.4], opacity: [0, 0.9, 0] }
              : { y: 0, opacity: 0.5 }
          }
          transition={
            animate
              ? { duration: 1.9, repeat: Infinity, ease: 'easeOut', delay: s.delay }
              : { duration: 0.2 }
          }
        />
      ))}
      {/* lid (dome + knob) lifts gently */}
      <motion.g
        animate={animate ? { y: [0, -1.3, 0] } : { y: 0 }}
        transition={
          animate
            ? { duration: 1.9, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.2 }
        }
      >
        <path d="M4.5 20a7.5 7.5 0 0 1 15 0" {...strokeProps} />
        <path d="M12 10.7V9.2" {...strokeProps} />
      </motion.g>
      {/* plate */}
      <path d="M3 20h18" {...strokeProps} />
    </motion.svg>
  )
}

/** Takeaway — shopping bag swinging from its handle. */
function TakeawayGlyph({ active, className }: GlyphProps) {
  const { animate, pop } = useGlyphMotion(active)
  return (
    <motion.svg
      viewBox="0 0 24 24"
      className={cn('h-6 w-6 shrink-0', className)}
      aria-hidden
      animate={pop}
    >
      <motion.g
        style={{ transformOrigin: '12px 4.5px' }}
        animate={animate ? { rotate: [0, -5, 4, -2, 0] } : { rotate: 0 }}
        transition={
          animate
            ? { duration: 2.2, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.2 }
        }
      >
        <path d="M7 4 4.5 7.5v12a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-12L17 4Z" {...strokeProps} />
        <path d="M4.5 7.5h15" {...strokeProps} />
        <path d="M15.5 11.5a3.5 3.5 0 0 1-7 0" {...strokeProps} />
      </motion.g>
    </motion.svg>
  )
}

/** Delivery — courier bike bobbing along with speed lines. */
function DeliveryGlyph({ active, className }: GlyphProps) {
  const { animate, pop } = useGlyphMotion(active)
  return (
    <motion.svg
      viewBox="0 0 24 24"
      className={cn('h-6 w-6 shrink-0', className)}
      aria-hidden
      animate={pop}
    >
      {/* speed lines */}
      {[
        { d: 'M1.5 10.5h3', delay: 0 },
        { d: 'M1 14h2.2', delay: 0.4 },
      ].map((s) => (
        <motion.path
          key={s.d}
          d={s.d}
          {...strokeProps}
          strokeWidth={1.5}
          animate={animate ? { x: [1.5, -1.5], opacity: [0, 1, 0] } : { opacity: 0 }}
          transition={
            animate
              ? { duration: 0.9, repeat: Infinity, ease: 'linear', delay: s.delay }
              : { duration: 0.2 }
          }
        />
      ))}
      {/* bike bobs as it rides */}
      <motion.g
        animate={animate ? { y: [0, -0.8, 0] } : { y: 0 }}
        transition={
          animate
            ? { duration: 1.1, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.2 }
        }
      >
        <circle cx="18.5" cy="17.5" r="3.5" {...strokeProps} />
        <circle cx="5.5" cy="17.5" r="3.5" {...strokeProps} />
        <circle cx="15" cy="5" r="1" {...strokeProps} />
        <path d="M12 17.5V14l-3-3 4-3 2 3h2" {...strokeProps} />
      </motion.g>
    </motion.svg>
  )
}

/** Fallback for custom order types — parcel with a soft pulse. */
function GenericGlyph({ active, className }: GlyphProps) {
  const { animate, pop } = useGlyphMotion(active)
  return (
    <motion.svg
      viewBox="0 0 24 24"
      className={cn('h-6 w-6 shrink-0', className)}
      aria-hidden
      animate={pop}
    >
      <motion.g
        style={{ transformOrigin: '12px 12px' }}
        animate={animate ? { scale: [1, 1.05, 1] } : { scale: 1 }}
        transition={
          animate
            ? { duration: 1.8, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.2 }
        }
      >
        <path d="M12 4 4 8v8l8 4 8-4V8l-8-4Z" {...strokeProps} />
        <path d="M4 8l8 4 8-4M12 12v8" {...strokeProps} strokeWidth={1.5} />
      </motion.g>
    </motion.svg>
  )
}

export function CounterOrderTypeGlyph({
  type,
  active,
  className,
}: GlyphProps & { type: string }) {
  const t = normalizeOrderTypeId(type)
  if (t === 'dine_in') return <DineInGlyph active={active} className={className} />
  if (t === 'takeout') return <TakeawayGlyph active={active} className={className} />
  if (t === 'delivery') return <DeliveryGlyph active={active} className={className} />
  return <GenericGlyph active={active} className={className} />
}
